import { ComplianceStandards } from "@nirvana/shared/dtos/types/compliance-reports/gz-console";
import { LicenseLink, LicenseMessageFn } from "./constant";
import { translateFn } from "../../utils/translator.helper";
import { ColumnKeys, Column } from '../complianceApp/types';
import { html } from "lit";
import { ListItem } from "../../ui/gzSingleSelectList/types";
import { ElementClass, elementClassListRemove } from "../../utils/htmlElement.utils";
import _ from 'lodash';
import { GridTitleConfig } from '../../grid/uiElements/gridViewTitle/types';
import { configureFavoriteIcon } from '../../grid/genericGridSmartViews/types';
import { defaultPageSizeOptions, SelectionStoreMode } from '../../grid/gridView/types';
import { dataGridStrings } from '../../grid/utils/gridStrings';
import { GridViewWebComponent } from "../..";
import { CompanyFilterDashboardDataProvider } from "../../ui/gzSingleSelectComplex/dataProvider/company/CompanyFilterDashboardDataProvider";
import { NirvanaErrorFeatureUnavailable } from "@nirvana/shared/error";
import { ComplianceAppView } from "../complianceApp/view/ComplianceAppView";
import { ExactMatchCustomValues } from "../../grid/utils/filters/types";
import { ComplianceErrorEmptyState } from "./error";
import { Feature } from "@nirvana/shared/constants";
import { NavigationHelper } from "@nirvana/shared/navigation";
import { LinkColor, LinkType, Target } from "../../ui/gzLinkComponent/types";

export const hideIfNoAccessToComplianceStandard = (hasAccessToComplianceStandard: boolean, value: any) => {
  return hasAccessToComplianceStandard ? value : '***';
}

export const addDisclaimer = (element: HTMLElement, value: string) => {
  element.innerHTML = value
  elementClassListRemove(element, ElementClass.HIDDEN);
}

export const complianceStandardOptions = Object.keys(ComplianceStandards)
  .map((key, index) => {  
    const title: string = ComplianceStandards[key].name;
    return {
      id: index,
      label: title,
      value: key,
      free: ComplianceStandards[key].free,
      title,
    };
});

export const complianceStandardFilterUiConfig = (grid: GridViewWebComponent<Column, ColumnKeys>) => {
  return {
    key: ColumnKeys.COMPLIANCE_STANDARD, 
    config: { 
      canDelete: false,
      async hasCompliancePremiumUsable() {
        let companyId = grid.filters.find(filter => filter[ColumnKeys.COMPANY])?.companyId.exactMatch;

        if (companyId === ExactMatchCustomValues.MY_COMPANY || !companyId) {
          companyId = Application.Main.getUserCompany()?.id;
        }

        const company = await CompanyFilterDashboardDataProvider.getCompanyById(companyId);
        return company.specifics.features.compliancePremiumUsable;
      },  
      itemSuffixRenderer: (listItem: ListItem, compliancePremiumUsable: boolean) => {
        if (!compliancePremiumUsable && !ComplianceStandards[listItem.value].free) {
          return html`
            <gz-tooltip class="compliance-select-suffix" tooltipText="${LicenseMessageFn.LICENSE_MESSAGE_PURCHASE_TOOLTIP()}">
              <gz-icon style="margin-top: var(--spacer)" color="secondary" size="18" class="compliance-select-suffix">
                <svg slot="custom-svg" fill="var(--chart-300-icon-buttons)" width="16" height="21" viewBox="0 0 16 21" xmlns="http://www.w3.org/2000/svg">
                  <g>
                    <path d="M2 21C1.45 21 0.979167 20.8042 0.5875 20.4125C0.195833 20.0208 0 19.55 0 19V9C0 8.45 0.195833 7.97917 0.5875 7.5875C0.979167 7.19583 1.45 7 2 7H3V5C3 3.61667 3.4875 2.4375 4.4625 1.4625C5.4375 0.4875 6.61667 0 8 0C9.38333 0 10.5625 0.4875 11.5375 1.4625C12.5125 2.4375 13 3.61667 13 5V7H14C14.55 7 15.0208 7.19583 15.4125 7.5875C15.8042 7.97917 16 8.45 16 9V19C16 19.55 15.8042 20.0208 15.4125 20.4125C15.0208 20.8042 14.55 21 14 21H2ZM2 19H14V9H2V19ZM8 16C8.55 16 9.02083 15.8042 9.4125 15.4125C9.80417 15.0208 10 14.55 10 14C10 13.45 9.80417 12.9792 9.4125 12.5875C9.02083 12.1958 8.55 12 8 12C7.45 12 6.97917 12.1958 6.5875 12.5875C6.19583 12.9792 6 13.45 6 14C6 14.55 6.19583 15.0208 6.5875 15.4125C6.97917 15.8042 7.45 16 8 16ZM5 7H11V5C11 4.16667 10.7083 3.45833 10.125 2.875C9.54167 2.29167 8.83333 2 8 2C7.16667 2 6.45833 2.29167 5.875 2.875C5.29167 3.45833 5 4.16667 5 5V7Z"/>
                  </g>
                </svg>
              </gz-icon>
            </gz-tooltip>`
        } 
      }
    } 
  }
}

export const controlComplianceScoreMap = {
  '100': {
    columnFilter: { label: `100%`, value: '100' },
    queryFilter: { min: 100, max: 100 },
  },
  '71-99': {
    columnFilter: { label: `71-99%`, value: '71-99' },
    queryFilter: { min: 71, max: 99 },
  },
  '21-70': {
    columnFilter: { label: `21-70%`, value: '21-70' },
    queryFilter: { min: 21, max: 70 },
  },
  '1-20': {
    columnFilter: { label: `1-20%`, value: '1-20' },
    queryFilter: { min: 1, max: 20 },
  },
  '0': { columnFilter: { label: `0%`, value: '0' }, queryFilter: { min: 0, max: 0 } },
};

export function setGridCommonProperties(
  grid: any,
  view: any,
  hasExternalFilters: boolean = false,
  initialViewWithoutExternalFilters: null | any = null,
  complianceAppView: ComplianceAppView,
) {
  // Copied from ./nirvana/apps/webcomponents/src/riskManagement/common/utils.ts
  // TODO: Remove parts that are irrelevant to compliance
  if (Application.Main.company.isCustomer()) {
    cleanupSmartView(view);
    if (initialViewWithoutExternalFilters) {
      cleanupSmartView(initialViewWithoutExternalFilters);
    }
  }

  grid.hasFilters = true;
  grid.enableRowClickEvent = true;

  if (hasExternalFilters && initialViewWithoutExternalFilters) {
    grid.defaultViewProps = _.cloneDeep(initialViewWithoutExternalFilters);
    grid.titleConfig = new GridTitleConfig(view.label + '*');
    grid.activeViewInitialProps = _.cloneDeep(initialViewWithoutExternalFilters);
    grid.smartViewDiscardButtonDisabledState = false;
  } else {
    grid.defaultViewProps = _.cloneDeep(view);
    grid.titleConfig = new GridTitleConfig(view.label);
    grid.activeViewInitialProps = _.cloneDeep(view);
    grid.smartViewDiscardButtonDisabledState = true;
  }

  grid.viewId = view.id;
  grid.columns = view.displayedColumns;

  grid.filters = view.filters;
  grid.isCompactViewChecked = view.compactView;
  grid.pageSize = view.pageSize;
  //column configs should be reset here otherwise they will not be overwritten
  grid.columnConfigs = [];
  grid.columnConfigs = view.columnConfigs ?? [];
  grid.favoriteIconConfig = configureFavoriteIcon(view);
  grid.hasSelectableRows = true;
  grid.hasPagination = true;
  grid.pageSizeOptions = defaultPageSizeOptions;
  grid.sortColumnName = ColumnKeys.CONTROL_ID;
  grid.sortDirection = 'ASC';
  grid.customStrings = {
    noSearchResultsString: translateFn('Start scan to find risks'),
    dataLoadingFailed: dataGridStrings().dataLoadingFailed,
  };
  grid.onDataLoadingFinishedWithError = (error: any) => {
    complianceAppView.setDefaultState();
    complianceAppView.setComplianceOverview({
      overallCompliance: 0,
      compliantCount: 0,
      nonCompliantCount: 0,
      ignoredCount: 0,
      totalChecks: 0,
    });
    complianceAppView.setDisableSmartViewAction(true);
    
    if (error instanceof NirvanaErrorFeatureUnavailable) {
      complianceAppView.setNoLicenseKeyDetectedView(false);
      complianceAppView.setDisableFilterScore(false);
    }

    if (error instanceof ComplianceErrorEmptyState) {
      complianceAppView.setNoFindingsView();
      complianceAppView.setDisableFilterScore(false);
    }
  };

  grid.selectionStoreConfig = {
    functionalMode: SelectionStoreMode.RECORDS,
  };
  grid.enableCSVExport = true;
  grid.smartViewsEnabled = true;
  grid.activeViewProps = view;
}

export function cleanupSmartView(view) {
  view.filters = view.filters.filter(filter => !filter['companyId']);
}

export function hasAccessToProductHub(): boolean {
  // only for cloud environment
  if (!Application.Main.product.isCloud() || Application.Main.product.isPrivateCloud()) {
    return false;
  }

  // check feature existence
  const hasInProductTrialsFeature = Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_IN_PRODUCT_TRIALS);

  // check company manager right
  const hasCompanyManagerRight = Application.Main.hasAnyRights([Constants.Rights.RIGHT_COMPANY_MANAGER]);

  // check has yearly payment plan
  const hasYearlyPaymentPlan = Application.Main.user.companyInfo.specifics['paymentPlan'] ===  Constants.PaymentPlanType.YEARLY;
  
  // check is customer
  const isCustomer = Application.Main.company.isCustomer();

  return hasInProductTrialsFeature && hasCompanyManagerRight && isCustomer && hasYearlyPaymentPlan;
}

export function getComplianceLearnMoreButton(): {
  show: boolean,
  text: string,
  link: string,
  linkAction: () => void,
} {
    const link = hasAccessToProductHub() ? LicenseLink.PRODUCT_HUB_LINK : LicenseLink.MSP_LICENSE_LINK;
    
    return {
      show: true,
      text: hasAccessToProductHub() ? LicenseMessageFn.LEARN_MORE() : LicenseMessageFn.CONTACT_PARTNER(),
      link,
      linkAction: () => hasAccessToProductHub() ? NavigationHelper.navigateTo(link.replace('#!', '')) : window.open(link, Target.SELF),
    }
}

export function disclaimerLink(link: string, buttonText: string) {
  return `<gz-link type="${LinkType.UNDERLINE}" color="${LinkColor.SECONDARY}" href="${link}" target="${Target.SELF}" id="complianceLicenseLinkId">${buttonText}</gz-link>`;
}